import type { Capability } from '@complan/domain';
import type { FilterScreen } from '@/shared/url/filter-context';
import {
  CalendarDays,
  ClipboardList,
  FileSpreadsheet,
  FolderCog,
  GaugeCircle,
  LayoutDashboard,
  ListChecks,
  ScrollText,
  Settings2,
  TrendingUp,
  Users,
  Waypoints,
  type LucideIcon,
} from 'lucide-react';

/**
 * The sidebar navigation model (UI spec §2.1).
 *
 * The five groups and their order are binding: Plan · Manpower · Maintain ·
 * Govern · Configure. Within a group the item order is the spec's.
 *
 * Two item kinds encode §0.1's scope decision without deleting the product's shape:
 * - `route`   — a screen that is built; it links somewhere.
 * - `placeholder` — a screen the click dummy specifies but a build defers. It renders as
 *   a visible-but-inert item so the shell reads as the whole product, per §0.1's
 *   "visual-only placeholders" note, and never navigates anywhere that has not been
 *   built.
 *
 * **Every item is a `route` as of `complan-governance` group 15**, so `placeholder`
 * currently has no users. The kind is kept rather than deleted because it is how the next
 * deferred screen enters the shell, and because `Sidebar` still renders the inert case -
 * removing it would mean removing that rendering and reinstating both later. The history,
 * since this paragraph has twice been wrong about it: the four Manpower items were
 * placeholders through Foundation and became routes in MVP 2; Governance and the
 * Management_Dashboard became routes in MVP 3.
 *
 * `editGated` marks the items §2.1 dims to opacity .35 for non-editing roles
 * (Initiative Intake · Sales Cycle Timing · Administration · Effort Matrix ·
 * Capacity Inputs); the gate reads a capability from the bootstrapped session.
 */
export type NavItemKind = 'route' | 'placeholder';

export interface NavItem {
  /** i18n key for the item label (R18.9); resolved in the Sidebar via `t`. */
  readonly labelKey: string;
  readonly icon: LucideIcon;
  readonly kind: NavItemKind;
  /** Present for `route` items only. */
  readonly to?: string;
  /** Dimmed for principals lacking this capability (UI spec §2.1 edit-gated set). */
  readonly editGatedCapability?: Capability;
  /**
   * The Filter_Context screen this item leads to, for the items that share one
   * (`complan-governance` R6.3).
   *
   * Present only on the participating screens. An item without it keeps the constant
   * `to` above, which is right for Intake, Administration and the Export centre:
   * carrying a filter selection into a wizard would suggest the wizard was scoped by
   * it. Where it *is* present, the sidebar replaces the constant target with one
   * carrying the current selection, so leaving a filtered screen does not discard the
   * filter.
   */
  readonly filterScreen?: FilterScreen;
}

export interface NavGroup {
  /** i18n key for the group label (R18.9); resolved in the Sidebar via `t`. */
  readonly labelKey: string;
  readonly items: readonly NavItem[];
}

export const NAVIGATION: readonly NavGroup[] = [
  {
    labelKey: 'nav.group.plan',
    items: [
      {
        // The Management_Dashboard (`complan-governance` R4.1). `filterScreen` is what
        // carries a selection here from the Calendar or the Initiative list (R6.3).
        labelKey: 'nav.item.dashboard',
        icon: LayoutDashboard,
        kind: 'route',
        to: '/dashboard',
        filterScreen: 'management-dashboard',
      },
      {
        labelKey: 'nav.item.commercialCalendar',
        icon: CalendarDays,
        kind: 'route',
        to: '/calendar',
        filterScreen: 'calendar',
      },
      {
        labelKey: 'nav.item.initiatives',
        icon: ListChecks,
        kind: 'route',
        to: '/initiatives',
        filterScreen: 'initiative-list',
      },
    ],
  },
  {
    labelKey: 'nav.group.manpower',
    items: [
      {
        labelKey: 'nav.item.capacityUtilisation',
        icon: GaugeCircle,
        kind: 'route',
        to: '/manpower/capacity-utilisation',
      },
      {
        labelKey: 'nav.item.demandExplorer',
        icon: TrendingUp,
        kind: 'route',
        to: '/manpower/demand-explorer',
      },
      {
        labelKey: 'nav.item.capacityInputs',
        icon: FileSpreadsheet,
        kind: 'route',
        to: '/manpower/capacity-inputs',
        editGatedCapability: 'capacity:write',
      },
      {
        labelKey: 'nav.item.effortMatrix',
        icon: Waypoints,
        kind: 'route',
        to: '/manpower/effort-matrix',
        editGatedCapability: 'manpower:write',
      },
    ],
  },
  {
    labelKey: 'nav.group.maintain',
    items: [
      {
        labelKey: 'nav.item.initiativeIntake',
        icon: ClipboardList,
        kind: 'route',
        to: '/intake',
        editGatedCapability: 'initiative:write',
      },
      {
        labelKey: 'nav.item.salesCycleTiming',
        icon: CalendarDays,
        kind: 'route',
        to: '/sales-cycle-timing',
        editGatedCapability: 'initiative:write',
      },
    ],
  },
  {
    labelKey: 'nav.group.govern',
    items: [
      {
        // `complan-governance` R3.1. `filterScreen` is what makes leaving a filtered
        // Calendar or Initiative list arrive here with the selection intact (R6.3).
        labelKey: 'nav.item.governance',
        icon: ScrollText,
        kind: 'route',
        to: '/governance',
        filterScreen: 'governance-dashboard',
      },
      { labelKey: 'nav.item.reportsExports', icon: FileSpreadsheet, kind: 'route', to: '/reports' },
    ],
  },
  {
    labelKey: 'nav.group.configure',
    items: [
      {
        labelKey: 'nav.item.administration',
        icon: Settings2,
        kind: 'route',
        to: '/admin',
        editGatedCapability: 'masterdata:write',
      },
    ],
  },
];

// Referenced only to keep the icon import list honest with the click dummy's
// iconography; unused icons would otherwise be flagged by the linter.
export const RESERVED_ICONS: readonly LucideIcon[] = [FolderCog, Users];
